import * as React from 'react';
import { Card } from '../../Card/card';
import './defaultData.scss';
import {
  Metadata,
  WorkspaceSetting,
  WorkspaceSettingProps
} from '../../../types';
import { getTreeSettingForFilter } from '../../Filters/RefereceInput/referenceInput';
import { TreeSelect, TreeSelectProps } from '../../TreeSelect/treeSelect';
import { NodeType } from '../../../enums/NodeType';
import { TreeFieldUtils } from '../../../utils/treeFieldUtils';
import { useActions } from '../workspaceSettingActions';
import { useAlert } from '../../Alert/alert';
import { getDefaultDataKey, getDefaultDataValue } from '../../../utils';
import { Toggler } from '../../Toggler/toggler';
import { useSelector } from '../../../utils/useSelector';
import { ReferenceSearch } from '../../ReferenceSearch/referenceSearch';

type Props = {
  setting: WorkspaceSetting;
};
type WSProps = WorkspaceSettingProps;

export const DefaultData = (props: Props) => {
  const { save, Alert } = useAlert(useActions());
  const [setting, setSetting] = React.useState<WSProps>(
    props.setting?.props || {}
  );

  const onChange =
    (nodeTypes: NodeType[], s?: WSProps) =>
    (m: Metadata, p: Metadata[], checked: boolean) => {
      const nodeType = nodeTypes.filter(l => m.labels.includes(l))[0];
      const key = getDefaultDataKey(nodeType);
      const _s = s || { ...setting };
      const values = (_s[key] || []).filter(v => v != m.props.refId);
      if (checked) values.push(m.props.refId);
      else {
        TreeFieldUtils.getChildren(m)?.forEach(c => {
          const _c = onChange(nodeTypes, _s);
          _c(c, [], false);
        });
      }
      _s[key] = values;
      if (checked && nodeTypes.length && p.length) {
        const _c = onChange(nodeTypes, _s);
        _c(p.pop(), p, checked);
      }
      if (!s) {
        setSetting(_s);
        save(
          nodeTypes.reduce((r, n) => {
            const k = getDefaultDataKey(n);
            r[k] = _s[k];
            return r;
          }, {})
        );
      }
    };

  const handleUseCaseToggle = (checked: boolean) => {
    setSetting({ ...setting, showTSPUseCases: checked });
    save({ showTSPUseCases: checked });
  };

  return (
    <div className="ws-setting-content default-data scrollable">
      <Alert />
      <div className="ws-setting-desc">
        {`Select and add the default set of Function(s)/Role(s) for your workspace`}
      </div>
      <Card header="Function & Role" expandable>
        <DefaultTreeData
          nodeType={NodeType.Role}
          selected={getSelected([NodeType.Function, NodeType.Role], setting)}
          onChange={onChange([NodeType.Function, NodeType.Role])}
        />
      </Card>
      <Card header="Research Model" expandable collapsed>
        <ReferenceSearch
          selected={getSelected([NodeType.ResearchModel], setting)}
          nodeType={NodeType.ResearchModel}
          minLength={0}
          multiple
          onChange={vals => {
            const key = getDefaultDataKey(NodeType.ResearchModel);
            const s = { [key]: vals.map(v => v.props.refId) };
            setSetting({ ...setting, ...s });
            save(s);
          }}
        />
      </Card>
      <Card header="Use Case" expandable collapsed>
        <Toggler
          label="TSP Use Cases"
          onChange={handleUseCaseToggle}
          checked={setting.showTSPUseCases}
        />
      </Card>
    </div>
  );
};

const getSelected = (nodeTypes: NodeType[], setting: WSProps) => {
  return getDefaultDataValue({ props: setting } as WorkspaceSetting, nodeTypes);
};

type DTDProps = {
  nodeType: NodeType;
  selected: string[];
} & Pick<TreeSelectProps<Metadata>, 'onChange'>;
const DefaultTreeData = ({ nodeType, selected, onChange }: DTDProps) => {
  const schemas = useSelector(s => s.planning.selectedWorkspace.schemas);
  const getAPI = useSelector(s => s.app.elFn.getAPI);
  const { getTree } = getAPI();
  const ts = getTreeSettingForFilter(nodeType, schemas);
  ts.settings.forEach(s => {
    s.hideCheckbox = false;
  });

  return (
    <TreeSelect<Metadata>
      getData={() => getTree(ts)}
      settings={ts.settings}
      selected={selected}
      isHidden={TreeFieldUtils.isHidden}
      matches={(d, txt) =>
        (d?.props?.name || d?.props?.description)
          ?.toLowerCase()
          .indexOf(txt.toLowerCase()) >= 0
      }
      inline={!!selected.length}
      getUniqueKey={TreeFieldUtils.getUniqueKey}
      getChildren={TreeFieldUtils.getChildren}
      onChange={onChange}
      renderData={TreeFieldUtils.renderData}
      highlightSearchText={true}
    />
  );
};
